import type { Field } from "../Field";
import { initField } from "../Field";
import type { GenericSchema, InferInput, InferOutput } from "valibot";
import {
  array,
  arrayAsync,
  boolean,
  fallback,
  literal,
  looseObject,
  maxLength,
  minLength,
  nullish,
  nullishAsync,
  number,
  object,
  objectAsync,
  optional,
  pipe,
  record,
  safeParse,
  string,
  transform,
  unknown,
  variant,
  variantAsync,
} from "valibot";
import {
  dataName,
  errorTranslation,
  gridFSFile,
  label,
  requireResource,
  requireText,
  resource,
} from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import {
  DECORATIVE_IMAGE_ALT,
  EXISTS_CONDITION_KEYS,
  GRIDFS_FILE_PREFIX,
  MAX_CHARACTERS_XL,
  MAX_CHARACTERS_XS,
  MAX_PINS,
} from "../../constants";
import { useFormServices } from "../../services/formServices";
import CustomerResourceInput from "../CustomerResourceInput";
import type { Accessor, Component, Setter } from "solid-js";
import { createSignal, For, Match, onCleanup, onMount, Show, Switch } from "solid-js";
import { isEmpty, isEqual, isNil, zipWith } from "lodash-es";
import MixedInput from "../../components/inputs/MixedInput";
import { SEARCHABLE_FIELDS, SUMMARIZABLE_FIELDS } from "../../utils/fieldUtil";
import { toFieldValueOptions } from "../../utils/selectOptionsUtil";
import { subformReference } from "../../utils/validation/idReferences";
import { requireResourceAsync, resourceAsync } from "../../utils/validation/async";
import {
  parseStringTemplate,
  parseStringTemplateForField,
  stringTemplateToString,
} from "../../utils/stringTemplateUtil";
import { stringTemplate } from "../../utils/validation/stringTemplate";
import { SEARCHABLE_VARIABLES, SUMMARIZABLE_VARIABLES, toVariableValueOption } from "../../variables/variableUtil";
import type { SubformDto } from "../../services/ApiService";
import { isFilledInFilter } from "../../pages/customer/form/filter";
import { createDynamicValueOptions } from "../../utils/menu/searchableMenuUtil";
import uuid from "../../utils/uuid";
import ButtonLink from "../../components/link/ButtonLink";
import { useModalManager } from "../../components/modal/ModalManager";
import DetailModal from "../../components/modal/DetailModal";
import logger from "../../utils/logger";
import { hasData } from "../fields";
import { renderFieldValue } from "../fieldValueRenderer";
import SimpleTable from "../../components/table/SimpleTable";
import { useCustomerState } from "../../state/customer";
import { Icon } from "../../components/icon/Icon";
import { fromPinIconUrl } from "../../utils/pinUtil";
import NumberBadge from "../../components/badge/NumberBadge";
import { createFileResource } from "../../utils/fileResourceUtil";
import { FieldButtonPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import { RemoteImage } from "../../components/image/Image";
import PinInput from "../PinInput";
import { keyFromSelector } from "i18next";
import missingImage from "../../assets/missing-image.svg";

const DtoSchema = looseObject({});

const ResourceSchema = objectAsync({
  default_image: resourceAsync(),
  pins: nullishAsync(
    arrayAsync(
      objectAsync({
        icon: variantAsync(
          "type",
          [
            object({
              type: literal("URL"),
            }),
            objectAsync({
              type: literal("RESOURCE"),
              value: requireResourceAsync(),
            }),
          ],
          errorTranslation(keyFromSelector(($) => $.VALIDATION_PIN_ICON_REQUIRED)),
        ),
      }),
    ),
    [],
  ),
});

const DomainSchema = object({
  label_text: label(),
  data_name: dataName(),
  default_image: resource(),
  allow_change_image: fallback(boolean(), true),
  pins: nullish(
    pipe(
      array(
        object({
          id: string(),
          name: requireText(MAX_CHARACTERS_XS),
          target_form_id: nullish(subformReference()),
          icon: variant("type", [
            object({
              type: literal("URL"),
              value: string(),
            }),
            object({
              type: literal("RESOURCE"),
              value: requireResource(),
            }),
          ]),
          itemMarkup: stringTemplate(SUMMARIZABLE_FIELDS, SUMMARIZABLE_VARIABLES, [], MAX_CHARACTERS_XL),
        }),
      ),
      minLength(1, errorTranslation(keyFromSelector(($) => $.VALIDATION_PINS_MIN_PINS))),
      maxLength(
        MAX_PINS,
        errorTranslation(
          keyFromSelector(($) => $.VALIDATION_PINS_MAX_PINS),
          { max: MAX_PINS },
        ),
      ),
    ),
    [],
  ),
  filename: stringTemplate(SEARCHABLE_FIELDS, SEARCHABLE_VARIABLES, [], MAX_CHARACTERS_XL, false),
}) satisfies GenericSchema<InferInput<typeof ResourceSchema>>;

const ValueSchema = object({
  image: gridFSFile(),
  pins: array(
    pipe(
      object({
        meta: object({
          location: object({
            x: number(),
            y: number(),
          }),
          scope: object({
            name: string(),
          }),
          description: optional(string()),
          icon: variant("type", [
            object({ type: literal("URL"), value: string() }),
            object({
              type: literal("RESOURCE"),
              value: string(),
            }),
          ]),
        }),
        data: record(string(), unknown()),
      }),
      transform((data) => ({ ...data, id: uuid() })),
    ),
  ),
});

export type PinFieldProperties = InferOutput<typeof DomainSchema>;
export type PinFieldDtoType = InferOutput<typeof DtoSchema>;
type ValueType = InferOutput<typeof ValueSchema>;
type PinValue = ValueType["pins"][number];
type Dimensions = { scaleX: number; scaleY: number; offsetLeft: number };

const PinField: Field<PinFieldProperties, PinFieldDtoType, ValueType> = {
  type: "pin",
  category: "media",
  widget: "com.moreapps:pin:1",
  schema: DomainSchema,
  valueSchema: ValueSchema,
  resourceSchema: ResourceSchema,
  hasData: true,
  defaultProperties: () => {
    const { t } = useTranslation();

    return {
      allow_change_image: true,
      default_image: null,
      pins: [
        {
          id: uuid(),
          name: "Pin 1",
          target_form_id: null,
          icon: { type: "URL", value: "https://cdn.moreapp.com/more-icons/check_green.png" },
          itemMarkup: [{ type: "literal", value: t(($) => $.FIELD_PROPERTY_PIN_ITEM_MARKUP_DEFAULT_VALUE) }],
        },
      ],
      filename: [],
    };
  },
  duplicateProperties: (properties, updateReference) => ({
    ...properties,
    filename: properties.filename.map(updateReference),
  }),
  ruleBehaviour: {
    conditions: { keys: EXISTS_CONDITION_KEYS, variant: "none" },
    actions: { variant: "none" },
  },
  filter: {
    type: "none",
    definition: [isFilledInFilter],
  },
  renderValue: (value, { fieldConfig, formVersion }) => {
    const [activePin, setActivePin] = createSignal<PinValue>();
    const { open } = useModalManager();

    return (
      <ol>
        <For each={value.pins}>
          {(pin, i) => (
            <li>
              <ButtonLink
                label={pin.meta.description ?? `${i() + 1}.`}
                onClick={() => {
                  setActivePin(pin);
                  open({
                    content: () => {
                      const { customerId } = useCustomerState();

                      const [dimensions, setDimensions] = createSignal<Dimensions | undefined>(undefined);

                      const imageId = value.image.split(GRIDFS_FILE_PREFIX).pop();
                      const [imageData] = createFileResource(
                        `/api/v1.0/customers/${customerId}/registrationFile/${imageId}/download`,
                      );

                      onCleanup(() => {
                        if (imageData.state !== "errored") {
                          const url = imageData()?.data;
                          if (!isNil(url)) {
                            URL.revokeObjectURL(url);
                          }
                        }
                      });

                      return (
                        <DetailModal
                          title={fieldConfig.properties.label_text}
                          iconTile={{ icon: PinField.icon, category: PinField.category }}
                          content={() => {
                            let img!: HTMLImageElement;

                            onMount(() => {
                              img.onload = () => {
                                // original image size
                                const naturalWidth = img.naturalWidth;
                                const naturalHeight = img.naturalHeight;

                                // size as rendered in the browser
                                const renderedWidth = img.clientWidth;
                                const renderedHeight = img.clientHeight;

                                const imgRect = img.getBoundingClientRect();
                                const wrapperRect = img.parentElement!.getBoundingClientRect();
                                const offsetLeft = imgRect.left - wrapperRect.left;

                                const scaleX = renderedWidth / naturalWidth;
                                const scaleY = renderedHeight / naturalHeight;

                                setDimensions({ scaleX, scaleY, offsetLeft });
                              };
                            });

                            return (
                              <div class="flex flex-col gap-4">
                                <div class="relative">
                                  <img
                                    ref={img}
                                    src={imageData()?.data}
                                    alt={imageData()?.filename}
                                    class="m-auto max-h-100 rounded-sm border border-subtle object-contain"
                                  />
                                  <Show when={dimensions()}>
                                    {(dimensions) => (
                                      <For each={value.pins}>
                                        {(pin, index) => (
                                          <Pin
                                            pin={pin}
                                            activePin={activePin}
                                            setActivePin={setActivePin}
                                            index={index}
                                            dimensions={dimensions}
                                          />
                                        )}
                                      </For>
                                    )}
                                  </Show>
                                </div>
                                <Show when={activePin()}>
                                  {(activePin) => {
                                    const pinConfig = () =>
                                      fieldConfig.properties.pins.find(
                                        ({ name }) => name === activePin().meta.scope.name,
                                      );

                                    if (isNil(pinConfig())) {
                                      logger.warn(
                                        `Unable to find pin configuration for '${activePin().meta.scope.name}'`,
                                      );
                                      return;
                                    }

                                    const pinFields = () =>
                                      formVersion.fields.filter(({ scope }) =>
                                        isEqual(pinConfig()?.target_form_id, scope),
                                      );

                                    const data = () =>
                                      pinFields()
                                        .filter(hasData)
                                        .map((subFieldConfig) => ({
                                          label: subFieldConfig.properties.label_text,
                                          value: renderFieldValue(
                                            activePin().data[subFieldConfig.properties.data_name],
                                            subFieldConfig,
                                            formVersion,
                                          ),
                                        }));

                                    return (
                                      <Show when={!isEmpty(data())}>
                                        <SimpleTable data={data()} />
                                      </Show>
                                    );
                                  }}
                                </Show>
                              </div>
                            );
                          }}
                        />
                      );
                    },
                  });
                }}
              />
            </li>
          )}
        </For>
      </ol>
    );
  },
  icon: "pin",
  titleKey: keyFromSelector(($) => $.PIN_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.PIN_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    default_image: keyFromSelector(($) => $.FIELD_PROPERTY_DEFAULT_IMAGE),
    allow_change_image: keyFromSelector(($) => $.FIELD_PROPERTY_ALLOW_CHANGE_IMAGE),
    pins: keyFromSelector(($) => $.FIELD_PROPERTY_PINS),
    filename: keyFromSelector(($) => $.FIELD_PROPERTY_FILE_NAME),
  },
  toDto: (domain, fields, variables) => {
    const { output } = safeParse(DtoSchema, {
      ...domain,
      // oxlint-disable-next-line @typescript-eslint/no-unused-vars
      pins: domain.pins.map(({ id, ...pin }) => ({
        ...pin,
        itemMarkup: stringTemplateToString(pin.itemMarkup, fields, variables),
      })),
      filename: stringTemplateToString(domain.filename, fields, variables),
    });
    return output as PinFieldDtoType;
  },
  fromDto: (dto) => {
    const { output } = safeParse(DomainSchema, dto);

    // Include dto form for mapping to linked subform
    const pins: PinFieldProperties["pins"] = zipWith(
      (output as PinFieldProperties).pins,
      dto.pins as { form?: SubformDto }[],
      (domainPin, dtoPin) => ({ ...domainPin, ...(isNil(dtoPin.form) ? {} : { form: dtoPin.form }) }),
    ).map((pin) => ({ ...pin, id: uuid() }));

    return { ...(output as PinFieldProperties), pins };
  },
  postProcessFromDto: (domain, fields, variables, scope) => ({
    ...domain,
    pins: domain.pins.map((pin) => ({
      ...pin,
      itemMarkup: parseStringTemplateForField(
        pin.itemMarkup,
        isNil(pin.target_form_id) ? [] : fields.filter((field) => pin.target_form_id?.equals(field.scope) ?? false),
        variables,
      ),
    })),
    filename: parseStringTemplateForField(
      domain.filename,
      fields.filter((field) => field.scope === scope),
      variables,
    ),
  }),
  PreviewContent: (props) => {
    const { t } = useTranslation();

    return (
      <PreviewContentLabel uid={props.uid} label={props.properties.label_text}>
        <Show
          when={props.properties.default_image}
          fallback={<FieldButtonPreview icon="camera" text={t(($) => $.PIN_FIELD_PREVIEW_ADD_BUTTON_LABEL)} />}
        >
          <RemoteImage size="lg" resourceId={props.properties.default_image} alt={DECORATIVE_IMAGE_ALT} cover={false} />
        </Show>
      </PreviewContentLabel>
    );
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService, variableService } = useFormServices();

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_CANVAS)}>
          <CustomerResourceInput
            required={false}
            label={t(props.propertyKeys.default_image)}
            value={props.data.properties.default_image ?? null}
            errorMessage={props.getError("default_image")}
            onSelect={(file) => props.setValue("default_image", file.id)}
            onClear={() => {
              props.setValue("default_image", null);
              props.setValue("allow_change_image", true);
            }}
          />
          <Show when={!isNil(props.data.properties.default_image)}>
            <Toggle
              block
              label={t(props.propertyKeys.allow_change_image)}
              checked={props.data.properties.allow_change_image}
              setChecked={(value) => props.setValue("allow_change_image", value)}
              description={t(($) => $.FIELD_PROPERTY_ALLOW_CHANGE_IMAGE_DESCRIPTION)}
            />
          </Show>
          <MixedInput
            label={t(props.propertyKeys.filename)}
            info={{
              title: t(props.propertyKeys.filename),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.filename }),
              description: t(($) => $.FIELD_PROPERTY_FILE_NAME_DESCRIPTION),
            }}
            value={props.data.properties.filename}
            parse={(value) =>
              parseStringTemplate(
                value,
                fieldService.getFieldsByTypes(SEARCHABLE_FIELDS),
                variableService.getVariablesByTypes(SEARCHABLE_VARIABLES),
              )
            }
            onChange={(value) => props.setValue("filename", value)}
            options={createDynamicValueOptions(t, {
              fields: fieldService.getFieldsByTypes(SEARCHABLE_FIELDS).flatMap((field) => toFieldValueOptions(field)),
              subforms: [],
              customVariables: variableService.getVariablesByTypes(SUMMARIZABLE_VARIABLES).map(toVariableValueOption),
              metaVariables: [],
            })}
            errorMessage={props.getError("filename")}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_PINS)}>
          <PinInput
            pins={props.data.properties.pins}
            onChange={(pins) => props.setValue("pins", pins)}
            error={props.getError("pins")}
            nestedErrors={props.getNestedErrors("pins")}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

type PinProps = {
  pin: PinValue;
  activePin: Accessor<PinValue | undefined>;
  setActivePin: Setter<PinValue | undefined>;
  index: Accessor<number>;
  dimensions: Accessor<Dimensions>;
};

const Pin: Component<PinProps> = (props) => (
  <button
    style={{
      left: `${props.dimensions().offsetLeft + props.dimensions().scaleX * props.pin.meta.location.x}px`,
      top: `${props.dimensions().scaleY * props.pin.meta.location.y}px`,
    }}
    class="group/pin-icon absolute top-0 -translate-1/2"
    onClick={() => props.setActivePin(props.pin)}
  >
    <Switch>
      <Match when={props.pin.meta.icon.type === "URL"}>
        <URLPinIcon {...props} />
      </Match>
      <Match when={props.pin.meta.icon.type === "RESOURCE"}>
        <ResourcePinIcon {...props} />
      </Match>
    </Switch>
  </button>
);

const URLPinIcon: Component<PinProps> = (props) => {
  const { icon, color, badgeColor, hoverColor } = fromPinIconUrl(props.pin.meta.icon.value);

  return (
    <>
      <div class="rounded-full bg-surface-default p-1">
        <Icon
          name={icon}
          type="solid"
          size="xl"
          classList={{
            "icon-tertiary": props.pin.id !== props.activePin()?.id,
            [hoverColor]: true,
            [color]: props.pin.id === props.activePin()?.id,
          }}
        />
      </div>
      <NumberBadge
        value={props.index() + 1}
        classList={{
          // oxlint-disable-next-line better-tailwindcss/no-restricted-classes
          "absolute top-[-5px] left-[-5px]": true,
          "bg-surface-status-subtle-neutral text-primary": props.pin.id !== props.activePin()?.id,
          [`${badgeColor} text-inverse`]: props.pin.id === props.activePin()?.id,
        }}
      />
    </>
  );
};

const ResourcePinIcon: Component<PinProps> = (props) => {
  const { customerId } = useCustomerState();

  const [iconData] = createFileResource(
    `/api/v1.0/customers/${customerId}/resources/${props.pin.meta.icon.value}/download/direct`,
  );

  onCleanup(() => {
    if (iconData.state !== "errored") {
      const url = iconData()?.data;
      if (!isNil(url)) {
        URL.revokeObjectURL(url);
      }
    }
  });

  return (
    <>
      <div class="size-8 rounded-sm bg-surface-default p-1">
        <Show when={iconData()} fallback={<img src={missingImage} alt={DECORATIVE_IMAGE_ALT} />}>
          {(iconData) => (
            <img
              class="size-6 rounded-px object-contain group-hover/pin-icon:opacity-100"
              classList={{
                "opacity-50": props.pin.id !== props.activePin()?.id,
              }}
              src={iconData().data}
              alt={DECORATIVE_IMAGE_ALT}
            />
          )}
        </Show>
      </div>
      <NumberBadge
        value={props.index() + 1}
        classList={{
          // oxlint-disable-next-line better-tailwindcss/no-restricted-classes
          "absolute top-[-5px] left-[-5px]": true,
          "bg-surface-status-subtle-neutral text-primary": props.pin.id !== props.activePin()?.id,
          "bg-surface-brand text-inverse": props.pin.id === props.activePin()?.id,
        }}
      />
    </>
  );
};

export default initField(PinField);
